/**
 * Hero-Bilderstrecke.
 *
 * Bewusst als Progressive Enhancement gebaut: Ohne JavaScript laeuft die
 * Strecke weiter als reine CSS-Ueberblendung, nur ohne Bedienung. Erst wenn
 * dieses Skript laedt, uebernimmt es die Steuerung – es setzt dazu die Klasse
 * "hero--interactive", die im CSS die Animation abschaltet und stattdessen
 * [data-active] ausblendet bzw. einblendet.
 *
 * Es gibt bewusst kein Framework und keine Abhaengigkeit: Das Theme soll nach
 * der Uebergabe ohne Build-Schritt wartbar bleiben.
 */
class HeroCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-hero-slide]')];
    this.count = Number(this.dataset.count || 0);

    // Unter zwei Bildern gibt es nichts zu steuern.
    if (this.count < 2) return;

    this.index = 0;
    this.interval = (Number(this.dataset.interval) || 6) * 1000;
    this.timer = null;

    this.classList.add('hero--interactive');

    // Die CSS-Ueberblendung wird per Inline-Style gesetzt (Dauer und
    // Verzoegerung haengen von der Bildanzahl ab). Inline schlaegt jede
    // Stylesheet-Regel – "animation: none" im CSS greift also nicht. Die
    // Deklarationen muessen hier entfernt werden, sonst laufen Automatik
    // und Steuerung gleichzeitig und es sind zwei Aussagen zugleich sichtbar.
    this.querySelectorAll('.hero__fade').forEach((el) => {
      el.style.removeProperty('animation-name');
      el.style.removeProperty('animation-duration');
      el.style.removeProperty('animation-delay');
    });

    this.#bind();
    this.#show(0);

    // Wer Bewegung reduziert haben will, bekommt nur das erste Bild und die
    // Bedienelemente – kein automatischer Wechsel.
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!this.reduced.matches) this.#play();

    // Im Hintergrundtab weiterlaufen zu lassen kostet nur Rechenzeit.
    this.onVisibility = () => (document.hidden ? this.#pause() : this.#playIfAllowed());
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  disconnectedCallback() {
    this.#pause();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  #bind() {
    this.querySelectorAll('[data-hero-goto]').forEach((el) => {
      el.addEventListener('click', () => this.#jump(Number(el.dataset.heroGoto)));
    });

    const prev = this.querySelector('[data-hero-prev]');
    const next = this.querySelector('[data-hero-next]');
    if (prev) prev.addEventListener('click', () => this.#jump(this.index - 1));
    if (next) next.addEventListener('click', () => this.#jump(this.index + 1));

    // Pfeiltasten, sobald die Strecke den Fokus hat
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.#jump(this.index - 1);
      if (e.key === 'ArrowRight') this.#jump(this.index + 1);
    });

    // Beim Ueberfahren anhalten, damit man in Ruhe lesen kann
    this.addEventListener('pointerenter', () => this.#pause());
    this.addEventListener('pointerleave', () => this.#playIfAllowed());
    this.addEventListener('focusin', () => this.#pause());
    this.addEventListener('focusout', () => this.#playIfAllowed());
  }

  /** Sprung per Bedienung: zeigen und den Automatiktakt neu starten, damit
   *  das gewaehlte Bild nicht sofort weiterspringt. */
  #jump(target) {
    this.#show((target + this.count) % this.count);
    this.#pause();
    this.#playIfAllowed();
  }

  #show(i) {
    this.index = i;
    this.slides.forEach((el) => {
      const active = Number(el.dataset.heroSlide) === i;
      el.toggleAttribute('data-active', active);

      // Nur die Punkte tragen aria-current; Bilder und Texte brauchen es nicht.
      if (el.hasAttribute('data-hero-goto')) {
        el.setAttribute('aria-current', active ? 'true' : 'false');
      }

      // Die ausgeblendeten Aussagen bleiben im Layout stehen – sie sind nur
      // durchsichtig. Ohne inert waeren ihre Schaltflaechen weiterhin per
      // Tabulator erreichbar und wuerden vom Screenreader vorgelesen, obwohl
      // nichts davon zu sehen ist. inert nimmt sie aus Fokus, Klick und
      // Vorlesereihenfolge, ohne die Weichblende zu stoeren.
      if (el.classList.contains('hero__caption')) {
        el.toggleAttribute('inert', !active);
      }
    });
  }

  #play() {
    this.timer = setInterval(() => this.#show((this.index + 1) % this.count), this.interval);
  }

  #playIfAllowed() {
    if (this.timer || (this.reduced && this.reduced.matches) || document.hidden) return;
    this.#play();
  }

  #pause() {
    clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('hero-carousel')) {
  customElements.define('hero-carousel', HeroCarousel);
}
